// @todo: Migrate to 'EditableField' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import EditableField from '@wandb/weave/common/components/EditableField';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {fuzzyMatchWithMapping} from '@wandb/weave/common/util/fuzzyMatch';
import {clsx} from 'clsx';
import _ from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
// @todo: Migrate to 'Link' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Link} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Input} from 'semantic-ui-react';

import {
  convertAndTransformAllMarkdownBlocks,
  editorHasMarkdownBlocks,
  revertConvertAllMarkdownBlocks,
} from '../../../components/Slate/plugins/markdown-blocks';
import {useTeamMembersQuery} from '../../../generated/graphql';
import {useApolloClient, useSelector} from '../../../state/hooks';
import * as ReportViewsSelectors from '../../../state/reports/selectors';
import * as ViewActions from '../../../state/views/actions';
import * as ViewApi from '../../../state/views/api';
import * as ViewHooks from '../../../state/views/hooks';
import {usePart, useViewRefAction} from '../../../state/views/hooks';
import * as ReportActions from '../../../state/views/report/actions';
import {ReportPageNormalized} from '../../../state/views/report/types';
import {useAdminModeActive} from '../../../util/admin';
import {ReportAuthor} from '../../../util/section';
import * as Urls from '../../../util/urls';
import {useWysiwygConversionContext} from '../../../util/userContext';
import {useLoadedReportContext} from '../LoadedReportContext';
import * as S from './ReportPageEditContentHeader.styles';

export const ReportPageEditContentHeader = () => {
  const {reportViewRef, view} = useLoadedReportContext();
  const admin = useAdminModeActive();

  const [parentViewLoaded, setParentViewLoaded] = useState(false);
  const [parentViewUser, setParentViewUser] = useState<{
    username: string;
    name?: string;
  } | null>(null);

  const client = useApolloClient();
  useEffect(() => {
    (async () => {
      if (!view.parentId) {
        setParentViewLoaded(true);
        return;
      }
      try {
        const loaded = await ViewApi.loadSingleViewById(
          client,
          view.parentId,
          'no-cache'
        );
        setParentViewUser(loaded.user);
      } catch (err) {
        console.error(err);
      } finally {
        setParentViewLoaded(true);
      }
    })();
  }, [client, view.parentId]);

  const reportID = view.id;
  if (reportID == null || view.partRef == null) {
    throw new Error('invalid state');
  }
  const viewPart = usePart(view.partRef) as ReportPageNormalized;
  const rename = useViewRefAction(reportViewRef, ViewActions.rename);
  const setNotes = useViewRefAction(reportViewRef, ViewActions.setDescription);
  const entityName = view.project?.entityName ?? view.entityName;
  const notes = view.description || '';

  const reportRef = useSelector(
    ReportViewsSelectors.getReportRef(reportViewRef)
  );

  const addAuthor = ViewHooks.useViewAction(reportRef, ReportActions.addAuthor);
  const removeAuthor = ViewHooks.useViewAction(
    reportRef,
    ReportActions.removeAuthor
  );
  const viewAuthors = viewPart.authors;
  const authors = viewAuthors ?? [];
  const originalAuthor = authors[0];
  useEffect(() => {
    if (!parentViewLoaded) {
      return;
    }
    if (viewAuthors == null || (!admin && viewAuthors.length === 0)) {
      const authorUser = parentViewUser != null ? parentViewUser : view.user;
      addAuthor(_.pick(authorUser, 'username', 'name'));
    }
  }, [
    admin,
    viewAuthors,
    addAuthor,
    view.user,
    parentViewUser,
    parentViewLoaded,
  ]);
  const [authorAdderActive, setAuthorAdderActive] = useState(false);

  const teamMembersQuery = useTeamMembersQuery({
    variables: {entityName},
  });

  let authorOptions: ReportAuthor[] = [];
  if (
    !teamMembersQuery.loading &&
    teamMembersQuery.data?.entity?.members != null
  ) {
    authorOptions = teamMembersQuery.data.entity.members as ReportAuthor[];
    if (!admin) {
      authorOptions = authorOptions.filter(
        m => m.username !== originalAuthor?.username
      );
    }
  }

  const {
    dismissed: wysiwygConversionDismissed,
    dismiss: dismissWysiwygConversion,
    loading: wysiwygConversionContextLoading,
  } = useWysiwygConversionContext();
  const [convertOngoing, setConvertOngoing] = useState(false);
  const [convertedToWysiwyg, setConvertedToWysiwyg] = useState(false);
  const convertToWysiwyg = useCallback(() => {
    if (convertOngoing) {
      return;
    }
    setConvertOngoing(true);
    setTimeout(() => {
      convertAndTransformAllMarkdownBlocks();
      setConvertedToWysiwyg(true);
      setConvertOngoing(false);
    });
  }, [convertOngoing]);
  const revertConvertToWysiwyg = useCallback(() => {
    if (convertOngoing) {
      return;
    }
    setConvertOngoing(true);
    setTimeout(() => {
      revertConvertAllMarkdownBlocks();
      setConvertedToWysiwyg(false);
      setConvertOngoing(false);
    });
  }, [convertOngoing]);
  const showWysiwygNotice =
    !wysiwygConversionContextLoading &&
    !wysiwygConversionDismissed &&
    (editorHasMarkdownBlocks() || convertedToWysiwyg);
  const wysiwygNotice = showWysiwygNotice ? (
    <S.WysiwygNotice>
      {!convertedToWysiwyg ? (
        <div>
          This report contains Markdown blocks. Would you like to convert them
          into{' '}
          <S.WysiwygNoticeLink href="https://www.notion.so/wandbai/WYSIWYG-Reports-FAQ-7d9527dc33e142b1ab66f431e21cdf88">
            WYSIWYG
          </S.WysiwygNoticeLink>
          ?
        </div>
      ) : (
        <S.WysiwygNoticeSuccess>
          <S.WysiwygNoticeSuccessIcon name="checkmark" />
          <div>
            Successfully converted to WYSIWYG.{' '}
            <S.WysiwygNoticeLink href="https://www.notion.so/WYSIWYG-Reports-FAQ-7d9527dc33e142b1ab66f431e21cdf88#e1a428c7328542d8ab07a28b5e27acc9">
              Some formatting may have changed
            </S.WysiwygNoticeLink>
            .
          </div>
        </S.WysiwygNoticeSuccess>
      )}
      <S.WysiwygNoticeActions>
        {!convertedToWysiwyg ? (
          <S.WysiwygConvert onClick={convertToWysiwyg}>
            {convertOngoing ? 'Converting...' : 'Convert'}
          </S.WysiwygConvert>
        ) : (
          <S.WysiwygRevert onClick={revertConvertToWysiwyg}>
            {convertOngoing ? 'Reverting...' : 'Revert'}
          </S.WysiwygRevert>
        )}
        <S.WysiwygDismiss onClick={dismissWysiwygConversion}>
          Dismiss
        </S.WysiwygDismiss>
      </S.WysiwygNoticeActions>
    </S.WysiwygNotice>
  ) : null;

  return (
    <div className="report-header-edit">
      <div className="report-header-edit__container">
        {wysiwygNotice}
        <EditableField
          className="report-header-edit__name-header"
          readOnly={false}
          asHeader="h1"
          placeholder="Report title"
          maxLength={128}
          value={view.displayName || ''}
          save={value => rename(value)}
        />
        <EditableField
          className="report-header-edit__description"
          multiline
          maxLength={512}
          readOnly={false}
          placeholder={'Add a description...'}
          value={notes}
          // Report metadata is loaded asynchronously so we need to support
          // dynamic updates to the description.
          updateValue={true}
          save={setNotes}
        />
        <div className="report-header-edit__authors">
          {!authorAdderActive
            ? authors.map(({username, name}, i) => (
                <React.Fragment key={username}>
                  <Link
                    className="report-header-edit__author"
                    to={Urls.profilePage(username)}>
                    {name || username}
                  </Link>
                  {i < authors.length - 1 && ', '}
                </React.Fragment>
              ))
            : !admin &&
              originalAuthor != null && (
                <Link
                  className="report-header-edit__author"
                  to={Urls.profilePage(originalAuthor.username)}>
                  {originalAuthor.name || originalAuthor.username}
                </Link>
              )}
          {authorOptions.length > 0 && (
            <>
              <LegacyWBIcon
                name="plus"
                className="report-header-edit__add-author"
                onClick={() => setAuthorAdderActive(true)}
              />
              <AuthorAdder
                active={authorAdderActive}
                added={
                  admin ? authors : authors.filter(a => a !== originalAuthor)
                }
                options={authorOptions}
                onAdd={addAuthor}
                onRemove={removeAuthor}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AUTHOR_ADDER_MAX_RESULTS = 10;

interface AuthorAdderProps {
  active: boolean;
  added: ReportAuthor[];
  options: ReportAuthor[];
  onAdd(a: ReportAuthor): void;
  onRemove(a: ReportAuthor): void;
}

const AuthorAdder = ({
  active,
  added,
  options,
  onAdd,
  onRemove,
}: AuthorAdderProps) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<Input | null>(null);

  useEffect(() => {
    if (!active) {
      return;
    }
    setTimeout(() => {
      if (inputRef.current != null) {
        inputRef.current.focus();
      }
    }, 100);
  }, [active]);

  const add = useCallback(
    (a: ReportAuthor) => {
      onAdd(a);
      setQuery('');
      if (inputRef.current != null) {
        inputRef.current.focus();
      }
    },
    [onAdd]
  );

  const isAdded = useCallback(
    (a: ReportAuthor) => added.some(author => a.username === author.username),
    [added]
  );

  const optionsToAdd = useMemo(() => {
    let results = options.filter(o => o.username != null && !isAdded(o));
    if (query.length > 0) {
      results = fuzzyMatchWithMapping(
        results,
        query,
        ({username, name}) => `${username},${name}`
      );
    }
    return results.slice(0, AUTHOR_ADDER_MAX_RESULTS);
  }, [options, query, isAdded]);

  return (
    <div className={clsx('report-header-edit__author-adder', {active})}>
      <div className="author-adder-fake-input">
        {added.map(author => (
          <div key={author.username} className="author-adder-added">
            {author.name || author.username}
            <LegacyWBIcon
              name="close"
              className="author-adder-remove"
              onClick={() => onRemove(author)}
            />
          </div>
        ))}
        <Input
          ref={el => (inputRef.current = el)}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setShowDropdown(false)}
          placeholder={'Select a team member'}
        />
      </div>
      <div
        className={clsx('author-adder-dropdown', {
          active: showDropdown && optionsToAdd.length > 0,
        })}>
        {optionsToAdd.map(author => (
          <div
            key={author.username}
            className="author-adder-dropdown-item"
            onKeyDown={({key}) => {
              if (key === 'Enter') {
                add(author);
              }
            }}
            onClick={() => add(author)}>
            {author.name || author.username}
          </div>
        ))}
      </div>
    </div>
  );
};
